import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { Button } from "../design/button";
import { Fieldset, FieldGroup, Label } from "../design/fieldset";
import { Heading } from "../design/heading";
import { Input } from "../design/input";
import { Field } from "@headlessui/react";
import {
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  LockClosedIcon,
  BuildingOffice2Icon,
  ShieldCheckIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";

import logo from "../assets/logo.png";
import { Text } from "../design/text";
import { Link } from "../design/link";

type LoginMode = "select" | "admin" | "supervisor";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<LoginMode>("select");
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/dashboard");
    } catch (err: any) {
      if (err.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else if (err.code === "auth/user-disabled") {
        setError("This account has been disabled. Please contact support.");
      } else if (err.code === "auth/user-not-found") {
        setError("No account found with this email address.");
      } else if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setError("Incorrect email or password. Please try again.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many failed attempts. Please try again later.");
      } else {
        setError("Unable to sign in. Please check your credentials and try again.");
      }
    }
    setIsSubmitting(false);
  };

  const handleSelectMode = (selected: LoginMode) => {
    if (selected === "supervisor") {
      navigate("/phone-login");
      return;
    }
    setMode(selected);
    setError(null);
    setEmail("");
    setPassword("");
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900">
      {/* Decorative background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-200 rounded-full opacity-20 blur-3xl dark:bg-blue-900" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-200 rounded-full opacity-20 blur-3xl dark:bg-indigo-900" />
      </div>

      <div className="relative w-full max-w-lg px-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={logo} alt="Visit Proof" className="w-20 h-20 mx-auto mb-4" />
          <Heading className="text-2xl font-bold text-zinc-900 dark:text-white">
            {mode === "select" ? "Welcome to VisitProof" : "Sign in"}
          </Heading>
          <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
            {mode === "select"
              ? "Choose how you'd like to sign in"
              : "Enter your credentials to continue"}
          </Text>
        </div>

        {mode === "select" ? (
          /* ============================================================ */
          /* ROLE SELECTION CARDS                                         */
          /* ============================================================ */
          <div className="space-y-4">
            <button
              onClick={() => handleSelectMode("admin")}
              className="w-full p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 dark:bg-zinc-900/80 dark:border-zinc-700/50 hover:shadow-xl hover:scale-[1.02] transition-all duration-200 text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/30 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/50 transition-colors">
                  <BuildingOffice2Icon className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <Text className="text-lg font-semibold text-zinc-900 dark:text-white">
                    Admin / Business Owner
                  </Text>
                  <Text className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    Sign in with email and password to manage cases, visits, reports, and billing.
                  </Text>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleSelectMode("supervisor")}
              className="w-full p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 dark:bg-zinc-900/80 dark:border-zinc-700/50 hover:shadow-xl hover:scale-[1.02] transition-all duration-200 text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50 transition-colors">
                  <ShieldCheckIcon className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="flex-1">
                  <Text className="text-lg font-semibold text-zinc-900 dark:text-white">
                    Supervisor / Monitor
                  </Text>
                  <Text className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    Sign in with your phone number to view schedules, document visits, and submit reports.
                  </Text>
                </div>
              </div>
            </button>

            {/* Register & Terms */}
            <div className="pt-4 text-center">
              <Text className="text-zinc-600 dark:text-zinc-400">
                Don't have an account?{" "}
                <Link href="/signup" className="font-semibold text-blue-600 hover:text-blue-500 dark:text-blue-400">
                  Create one now
                </Link>
              </Text>
            </div>
          </div>
        ) : (
          /* ============================================================ */
          /* ADMIN LOGIN FORM                                             */
          /* ============================================================ */
          <form
            method="post"
            className="w-full p-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:bg-zinc-900/80 dark:border-zinc-700/50"
            onSubmit={handleSubmit}
          >
            {/* Back button */}
            <button
              type="button"
              onClick={() => setMode("select")}
              className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 mb-6 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back
            </button>

            {/* Error Alert */}
            {error && (
              <div className="p-4 mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{error}</span>
                </div>
              </div>
            )}

            <Fieldset>
              <FieldGroup className="space-y-5">
                {/* Email */}
                <Field>
                  <Label htmlFor="email" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    Email address
                  </Label>
                  <div className="relative mt-1.5">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none z-10">
                      <EnvelopeIcon className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
                    </div>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </Field>

                {/* Password */}
                <Field>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                      Password
                    </Label>
                    <Link
                      href="/forgot-password"
                      className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative mt-1.5">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none z-10">
                      <LockClosedIcon className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
                    </div>
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="pl-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 z-10 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
                    >
                      {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                    </button>
                  </div>
                </Field>
              </FieldGroup>

              <Button
                type="submit"
                color="blue"
                className="w-full mt-6 py-2.5"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  "Sign in"
                )}
              </Button>
            </Fieldset>

            <div className="mt-6 text-center">
              <Text className="text-zinc-600 dark:text-zinc-400">
                Don't have an account?{" "}
                <Link href="/signup" className="font-semibold text-blue-600 hover:text-blue-500 dark:text-blue-400">
                  Create one now
                </Link>
              </Text>
            </div>
          </form>
        )}

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-zinc-500 dark:text-zinc-500">
          By signing in, you agree to our{" "}
          <a href="/terms" className="underline hover:text-zinc-700 dark:hover:text-zinc-300">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="/privacy" className="underline hover:text-zinc-700 dark:hover:text-zinc-300">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
